'use client';

import { Button } from '@/components/ui/Button';
import { useUser } from '@/contexts/UserContext';
import { Building2, MessageSquare } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

interface WrongCompanyRequestClientProps {
  onClose?: () => void;
}

export default function WrongCompanyRequestClient({
  onClose,
}: WrongCompanyRequestClientProps = {}) {
  const user = useUser();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [comments, setComments] = React.useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const supportData = {
        name: user.name || 'User',
        email: user.email || '',
        subject: 'Wrong Company Association - Request for Correction',
        message: `Current Company: ${user.companyName || 'Unknown'}

Comments:
${comments || 'No additional comments provided.'}

Please update my company association accordingly.`,
      };

      const emailResponse = await fetch('/api/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(supportData),
      });

      if (emailResponse.ok) {
        toast.success('Request submitted successfully!', {
          description: 'We will review and update your company association soon.',
        });

        setComments('');

        if (onClose) {
          onClose();
        }
      } else {
        throw new Error('Failed to send support request');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('An error occurred while submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Wrong Company Association</h3>
        <p className="text-sm text-muted-foreground">
          You are currently associated with <strong>{user.companyName || 'Unknown'}</strong>. If this
          is incorrect, please provide details below.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Comments */}
        <div className="space-y-2">
          <label htmlFor="comments" className="flex items-center gap-2 text-sm font-medium">
            <MessageSquare className="h-4 w-4" />
            Additional comments (optional)
          </label>
          <textarea
            id="comments"
            value={comments}
            onChange={e => setComments(e.target.value)}
            placeholder="Please provide any additional information about why you need to change your company association..."
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full"
          size="lg"
        >
          {isSubmitting ? (
            <>
              <span className="mr-2">Submitting...</span>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </>
          ) : (
            <>
              <Building2 className="mr-2 h-4 w-4" />
              Submit Request
            </>
          )}
        </Button>
      </form>
    </div>
  );
}

